import {useOnViewportChange, useStore} from '@xyflow/react';
import {useLayoutEffect, useRef} from 'react';

const ZOOM_END_DEBOUNCE_MS = 100;

/**
 * Syncs zoom level to CSS variables on the ReactFlow container.
 *
 * - var(--canvas-zoom): current zoom (e.g. 0.5, 1, 2)
 * - var(--inverse-zoom): 1/zoom for counter-scaling (e.g. 2, 1, 0.5)
 *
 * Every var update on the root cascades into every descendant whose computed
 * style references it (~hundreds of elements at scale). To avoid running the
 * cascade on every d3-zoom event during a continuous gesture, we:
 *
 *  1. Mark the container with `data-canvas-zooming="true"` on the first event
 *     of a zoom gesture. CSS uses this to hide zoom-counter-scaled chrome
 *     so the user doesn't see it rendering at stale sizes.
 *  2. Debounce the setProperty: only write the new vars 150ms after the last
 *     viewport change. Result: ~one cascade per gesture instead of one per
 *     d3-zoom event (which can be 60+ per second on trackpads).
 *
 * Pan-only events (zoom unchanged) bypass the gesture logic entirely.
 */
export const ZoomCSSVariableSync = () => {
  const container = useStore((state) => state.domNode);
  const debounceRef = useRef<number | null>(null);
  const lastZoomRef = useRef<number>(1);

  useLayoutEffect(() => {
    if (!container) return;
    container.style.setProperty('--canvas-zoom', '1');
    container.style.setProperty('--inverse-zoom', '1');
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      container.removeAttribute('data-canvas-zooming');
    };
  }, [container]);

  useOnViewportChange({
    onChange: (viewport) => {
      if (!container) return;
      if (viewport.zoom === lastZoomRef.current) return;
      lastZoomRef.current = viewport.zoom;

      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      } else {
        container.setAttribute('data-canvas-zooming', 'true');
      }

      debounceRef.current = window.setTimeout(() => {
        debounceRef.current = null;
        const z = lastZoomRef.current;
        container.style.setProperty('--canvas-zoom', String(z));
        container.style.setProperty('--inverse-zoom', String(1 / z));
        container.removeAttribute('data-canvas-zooming');
      }, ZOOM_END_DEBOUNCE_MS);
    },
  });

  return null;
};
